import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useUser } from './UserContext';
import {
  canAccessTenant as checkTenantAccess,
  getMembershipForTenant,
  loadMembershipsForUser,
  resolveInitialTenantId,
} from '../services/membershipService';

const MembershipContext = createContext(null);

export function useMembership() {
  const context = useContext(MembershipContext);
  if (!context) {
    throw new Error('useMembership must be used within MembershipProvider');
  }
  return context;
}

/** Read-only membership — validates tenant access after login. */
export function MembershipProvider({ children }) {
  const { user } = useUser();
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user?.uid) {
      setMemberships([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    loadMembershipsForUser(user.uid)
      .then((loaded) => {
        if (!cancelled) {
          setMemberships(loaded);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  const canAccessTenant = useCallback(
    (tenantId) => checkTenantAccess(memberships, tenantId),
    [memberships]
  );

  const resolveTenantId = useCallback(
    (preferredTenantId) => resolveInitialTenantId(memberships, preferredTenantId),
    [memberships]
  );

  const membershipForTenant = useCallback(
    (tenantId) => getMembershipForTenant(memberships, tenantId),
    [memberships]
  );

  const value = useMemo(
    () => ({
      memberships,
      loading,
      hasMemberships: memberships.length > 0,
      canAccessTenant,
      resolveTenantId,
      membershipForTenant,
    }),
    [memberships, loading, canAccessTenant, resolveTenantId, membershipForTenant]
  );

  return (
    <MembershipContext.Provider value={value}>
      {children}
    </MembershipContext.Provider>
  );
}

export default MembershipProvider;
